// Oath is purely client-side
export const OATH_KEY = (id: string) => `${id}-oath`;

/**
 * Object pronoun for a gender: "him" for male, "her" for female, "them"
 * otherwise (unknown/empty).
 */
export const getPronoun = (gender: string): string => {
  switch (gender?.toLowerCase()) {
    case "male":
      return "him";
    case "female":
      return "her";
    default:
      return "them";
  }
};

export const CONDITIONAL_QUESTIONS = ["Duration", "MaritalStatus"] as const;
export type ConditionalQuestion = (typeof CONDITIONAL_QUESTIONS)[number];

export type ConditionalAnswers = Partial<Record<ConditionalQuestion, string>>;

// Which conditional questions each screen owns. The relationship questionnaire
// asks Duration; the "about you" screen asks Marital Status.
export const RESPOND_QUESTIONS = ["Duration"] as const;
export const DETAILS_QUESTIONS = ["MaritalStatus"] as const;

interface RelationshipFlow {
  /** Conditional questions asked between Relationship and Comments, in display order */
  questions: readonly ConditionalQuestion[];
  /** Values submitted for the questions this relationship skips */
  defaults: Readonly<ConditionalAnswers>;
}

const FULL_FLOW: RelationshipFlow = {
  // Removed MaritalStatus as that's not bieng asked anymore, if needed just add it back in array
  questions: ["Duration"],
  defaults: { MaritalStatus: "not-applicable" },
};

const FAMILY_FLOW: RelationshipFlow = {
  questions: [],
  defaults: { Duration: "forever", MaritalStatus: "not-applicable" },
};

const PARENT_FLOW: RelationshipFlow = {
  questions: [],
  defaults: { Duration: "forever", MaritalStatus: "not-applicable" },
};

// Keys must match the option values in RELATIONSHIP_OPTIONS (constants.ts),
// including the existing "acquintance" spelling — fix both together or neither.
export const RELATIONSHIP_FLOWS: Record<string, RelationshipFlow> = {
  parent: PARENT_FLOW,
  sibling: FAMILY_FLOW,
  cousin: FAMILY_FLOW,
  family: FAMILY_FLOW,
  friend: FULL_FLOW,
  colleague: FULL_FLOW,
  acquaintance: FULL_FLOW,
};

const getFlow = (relationship: string): RelationshipFlow | undefined =>
  RELATIONSHIP_FLOWS[relationship];

/**
 * Reveal frontier: the flow's questions (limited to `scope`) shown so far, one
 * at a time — every already-answered question plus the first not-yet-answered
 * one. Returns `[]` for an unknown/empty relationship. `scope` limits the result
 * to a single screen's questions (e.g. `RESPOND_QUESTIONS` / `DETAILS_QUESTIONS`).
 */
export const getVisibleQuestions = (
  relationship: string,
  answers: ConditionalAnswers,
  scope: readonly ConditionalQuestion[] = CONDITIONAL_QUESTIONS,
): readonly ConditionalQuestion[] => {
  const flow = getFlow(relationship);
  if (!flow) return [];
  const visible: ConditionalQuestion[] = [];
  for (const question of flow.questions) {
    if (!scope.includes(question)) continue; // not this screen's question
    visible.push(question);
    if (!answers[question]) break; // stop at the first unanswered question
  }
  return visible;
};

/**
 * True when the relationship is known and every conditional question it asks
 * (within `scope`) is answered. Gates the Comments field (and, for Parent, is
 * true immediately).
 */
export const areConditionalsComplete = (
  relationship: string,
  answers: ConditionalAnswers,
  scope: readonly ConditionalQuestion[] = CONDITIONAL_QUESTIONS,
): boolean => {
  const flow = getFlow(relationship);
  if (!flow) return false;
  return flow.questions
    .filter((question) => scope.includes(question))
    .every((question) => !!answers[question]);
};

/**
 * Answers to submit: the user's answer for questions this relationship asks,
 * the flow's default for questions it skips.
 */
export const resolveAnswers = (
  relationship: string,
  answers: ConditionalAnswers,
): Record<ConditionalQuestion, string> => {
  const flow = getFlow(relationship);
  const resolved = {} as Record<ConditionalQuestion, string>;
  for (const question of CONDITIONAL_QUESTIONS) {
    resolved[question] = flow?.questions.includes(question)
      ? (answers[question] ?? "")
      : (flow?.defaults[question] ?? answers[question] ?? "");
  }
  return resolved;
};

/**
 * Screen 1 (relationship questionnaire): complete when Comments is filled and
 * Duration is answered for relationships that ask it. `areConditionalsComplete`
 * returns false for an unknown relationship, so a relationship must be picked.
 */
export const isRespondComplete = (
  relationship: string,
  answers: ConditionalAnswers & { Comments?: string },
): boolean => {
  if (!answers.Comments?.trim()) return false;
  return areConditionalsComplete(relationship, answers, RESPOND_QUESTIONS);
};

/**
 * Screen 2 ("about you"): complete when Name and Phone are filled and Marital
 * Status is answered for relationships that ask it (Parent skips it).
 */
export const isDetailsComplete = (
  relationship: string,
  answers: ConditionalAnswers & { Name?: string; Phone?: string },
): boolean => {
  if (!answers.Name?.trim()) return false;
  if (!answers.Phone?.trim()) return false;
  return areConditionalsComplete(relationship, answers, DETAILS_QUESTIONS);
};
